/**
 * Pending changes tray — Phase 3 (2026-06-01), labelled since PR 8.
 *
 * Shows the current operator's staged manual edits plus a global conflict
 * banner when two operators have edits queued against the same key.
 * Clicking Apply fires the API call + writes an audit row; Discard drops
 * the staged change.
 *
 * Mounted from the global header. Hidden by default; the "Pending changes"
 * button (count + conflict warning) opens the side panel, and so does
 * OPEN_PENDING_TRAY_EVENT (the bell's "You have N pending changes" item).
 * Escape closes the confirm step first, then the drawer — the overlay kit's
 * layers (components/overlay/useOverlay.ts) — and focus goes back to the
 * button.
 *
 * Polling: the pending list every 5 s while open, 30 s while closed. The
 * "Recent changes" history (#98) is collapsed by default and read only
 * while it is expanded (pendingTrayModel.ts).
 */
import { useEffect, useMemo, useRef, useState, useCallback, type RefObject } from 'react'
import { Link } from 'react-router-dom'
import {
  Inbox, AlertTriangle, X, Check, Trash2, Loader2, Send, CheckCircle2, RotateCcw, Lightbulb,
  ChevronDown, ChevronRight,
} from 'lucide-react'
import { apiEntity, EDITS_APPLIED_EVENT, type ManualEdit, type AppliedEditDetail, type NewCampaignJob } from '../lib/api'
import { isApiError } from '../lib/query/apiError'
import { useActiveOperator } from './OperatorSelector'
import { useApiQuery } from '../lib/query/useApiQuery'
import { keys } from '../lib/query/keys'
import { launchApi } from '../lib/launchApi'
import { cn } from '../lib/cn'
import { CopyId } from './CopyId'
import { useOverlay } from './overlay/useOverlay'
import {
  loadPendingEdits, loadRecentEdits, readRecentOpen, recentQueryEnabled, subscribeOpenTray,
  trayButtonLabel, traySubtitle, writeRecentOpen, type RecentScope,
} from './pendingTrayModel'
import { Badge } from './ui'
import { CreateAllButton, CreateAllConfirmBody } from './PendingTrayCreate'
// entityIdLabel (Target ID / Keyword ID / …), the row headline and the
// Apply-all filter live in a plain module so they can be tested. PR 16 / 17: a
// listing image update or a Launch tab listing draft is a HELD row while the
// server's listing updates are switched off — no Apply button, a chip
// instead, and never part of Apply all.
import {
  campaignConfirmRows, canRetryFailed, createAllIds, creatingToastText, entityIdLabel,
  entityIdText, harvestRowLines, heldRowChip, heldSummary, isHeldTrayRow, jobOutcome, outcomeBucket,
  sendableIds, trayHeadline, ownsRun, createRefusalMessage,
} from './pendingTrayRows'

// ── Recent-edits section (2026-07-07 owner ask) ────────────────────────────
// The tray's activity log: everything that ISN'T the viewer's own actionable
// pending queue (that stays in the section above), scoped by a Mine/Others/All
// toggle. Read-only — Apply/Discard never appear on another operator's rows.

const RECENT_STATUS_STYLE: Record<string, string> = {
  applied:          'bg-accent-soft text-accent-soft-fg border-accent-edge',
  staged_no_writes: 'bg-warning-soft text-warning-soft-fg border-warning-edge',
  pending:          'bg-warning-soft text-warning-soft-fg border-warning-edge',
  failed:           'bg-danger-soft text-danger-soft-fg border-danger-edge',
  discarded:        'bg-surface-2 text-ink-muted border-edge',
  superseded:       'bg-surface-2 text-ink-muted border-edge',
}
const RECENT_STATUS_FALLBACK = 'bg-surface-2 text-ink-muted border-edge'

function recentStatusLabel(s: string): string {
  return s === 'staged_no_writes' ? 'staged (writes off)' : s
}

// Compact relative timestamp for the activity rows ("4m ago" / "3h ago" /
// "Jul 5"). Falls back to the raw string when unparseable.
function relTime(iso: string | null): string {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return iso
  const mins = Math.round((Date.now() - t) / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// UI overhaul PR-2: tray cadences and a stable empty list.
const TRAY_OPEN_POLL_MS = 5_000
const TRAY_CLOSED_POLL_MS = 30_000
const NO_EDITS: ManualEdit[] = []
// PR 24: how often the "Create all" run is asked for its progress.
const CREATE_POLL_MS = 2_000

export default function PendingTray() {
  const [active] = useActiveOperator()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<number | 'batch' | 'create' | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  // Confirm-before-push: the set of edit ids the operator is about to push.
  // null = no confirm open. A single Apply opens with [id]; Apply-all opens
  // with every pending id. No push fires until the operator confirms.
  const [confirmIds, setConfirmIds] = useState<number[] | null>(null)
  const [liveWrites, setLiveWrites] = useState<boolean | null>(null)
  // Which action opened the confirm: a single row (keep the tray open after) vs
  // Apply-all (close the tray on full success), per the owner's spec.
  // 'create' (PR 24) is "Create all N campaigns": the confirm lists every campaign.
  const [confirmMode, setConfirmMode] = useState<'single' | 'all' | 'create'>('single')
  // Bottom-right toast tracking the push lifecycle (working → applied/staged/failed).
  const [toast, setToast] = useState<
    | { kind: 'working'; count: number }
    | { kind: 'creating'; done: number; total: number; label: string | null }
    | { kind: 'note'; text: string }
    | { kind: 'applied'; applied: number; staged: number; skipped?: number }
    | { kind: 'failed'; failed: number; total: number }
    | null
  >(null)
  // Edits whose push FAILED — kept visible (with the error) so they don't
  // silently vanish; the owner can retry (re-stage) or dismiss them.
  const [failedEdits, setFailedEdits] = useState<Array<ManualEdit & { error_msg: string | null }>>([])
  // PR 24: the "Create all" run being watched. The server creates the campaigns
  // one after another in a background job; this polls it until it is done.
  const [jobId, setJobId] = useState<string | null>(null)
  // The rows the run was started with, kept so a finished run can name the
  // ones that failed (they have left the pending list by then).
  const jobRowsRef = useRef<Map<number, ManualEdit>>(new Map())
  const jobRefusedRef = useRef<Array<ManualEdit & { error_msg: string | null }>>([])

  // Recent-activity section state: everyone's edit history below the pending
  // queue, scoped by the Mine/Others/All toggle (default All — the point is
  // seeing what OTHER operators changed). Collapsed by default (#98) and
  // remembered for this browser session only.
  const [recentScope, setRecentScope] = useState<RecentScope>('all')
  const [recentOpen, setRecentOpen] = useState<boolean>(readRecentOpen)
  function toggleRecent() {
    const next = !recentOpen
    writeRecentOpen(next)
    setRecentOpen(next)
  }

  // UI overhaul PR-2 / PR 8: the pending list is one shared query per
  // operator — every 5 s while the tray is open, 30 s while closed, and only
  // while this pane is visible. The history is its own query, enabled only
  // while the drawer is open AND "Recent changes" is expanded. Both keys sit
  // under keys.pendingEditsAll(), so every existing invalidation reaches them.
  const tray = useApiQuery(
    keys.pendingEdits({ operator: active, list: 'pending' }),
    () => loadPendingEdits(active ?? ''),
    { enabled: !!active, poll: open ? TRAY_OPEN_POLL_MS : TRAY_CLOSED_POLL_MS, keepPrevious: true },
  )
  const recentQ = useApiQuery(
    keys.pendingEdits({ operator: active, list: 'recent', scope: recentScope }),
    () => loadRecentEdits(active ?? '', recentScope),
    { enabled: recentQueryEnabled(active, open, recentOpen), poll: TRAY_OPEN_POLL_MS, keepPrevious: true },
  )
  // The switches that hold staged harvests (PR 5b): read only while the drawer
  // is open and the queue has a harvest row, from the same status the confirm
  // below reads. Unknown (not loaded, or an older server) holds nothing.
  const hasHarvestRows = !!active && (tray.data?.items ?? NO_EDITS).some(i => i.entity_type === 'harvest')
  const writesQ = useApiQuery(keys.harvest.writes(), () => apiEntity.getLiveWritesStatus(),
    { enabled: open && hasHarvestRows, staleTime: 30_000 })
  const trayLiveWrites = writesQ.data ?? null
  const items: ManualEdit[] = active ? (tray.data?.items ?? NO_EDITS) : NO_EDITS
  const recent: ManualEdit[] = active ? (recentQ.data ?? NO_EDITS) : NO_EDITS
  /** Pending rows held back because they belong to the Suggests queue — shown
   *  as one line with a link, never as rows this tray can apply. */
  const suggestsQueued: number = active ? (tray.data?.suggestsQueued ?? 0) : 0
  const error = actionError ?? (tray.isError ? tray.error.message : null)
  const { refetch: refetchTray } = tray
  const { refetch: refetchRecent } = recentQ
  const refresh = useCallback(async () => {
    await Promise.all([refetchTray(), recentOpen ? refetchRecent() : null])
  }, [refetchTray, refetchRecent, recentOpen])

  // The bell's "You have N pending changes" item opens the tray from anywhere.
  useEffect(() => subscribeOpenTray(window, () => setOpen(true)), [])

  // Escape, focus return and the focus trap come from the overlay kit. The
  // confirm attaches after the drawer, so it is the top layer while open:
  // one Escape closes the confirm, the next closes the drawer.
  // D5 (PR 12): the drawer has NO full-screen backdrop any more. That backdrop
  // sat over the header, so a click on the search box closed the tray and
  // never reached the box. The drawer is not modal: a mousedown outside it
  // closes it (the tray's own button excepted: it is the anchor) and the
  // click goes through, so the search box opens the palette in one click.
  const buttonRef = useRef<HTMLButtonElement>(null)
  const drawerRef = useRef<HTMLDivElement>(null)
  const confirmRef = useRef<HTMLDivElement>(null)
  useOverlay(open && !!active, drawerRef, {
    onClose: () => setOpen(false), anchorRef: buttonRef, modal: false, closeOnOutsideClick: true,
  })
  useOverlay(confirmIds != null, confirmRef, {
    onClose: () => setConfirmIds(null), modal: true, closeOnOutsideClick: false,
    dismissible: busy == null,
  })

  // PR 17: are listing updates switched on? Until the server says so (or when
  // the read fails) a listing / listing image row is held, never offered Apply.
  const listingWrites = useApiQuery(keys.launch.status(), () => launchApi.status(),
    { staleTime: 60_000, retry: 0 }).data?.listing_writes === true
  // One object for every switch that holds a tray row (harvest + listing updates).
  const writes = useMemo(() => ({ ...trayLiveWrites, listing_writes: listingWrites }), [trayLiveWrites, listingWrites])

  // Read the live-writes posture once the confirm modal opens so the banner
  // tells the operator whether the push will hit Amazon or stage no-writes.
  useEffect(() => {
    if (confirmIds == null) return
    let cancelled = false
    apiEntity.getLiveWritesStatus()
      // Tray applies are operator manual edits, so they reach Amazon when EITHER
      // gate is on (manual_pushes_to_amazon = live_writes OR manual_writes).
      .then(r => { if (!cancelled) setLiveWrites(r.manual_pushes_to_amazon ?? r.live_writes_enabled) })
      .catch(() => { if (!cancelled) setLiveWrites(null) })
    return () => { cancelled = true }
  }, [confirmIds])

  // Lightweight polling so the badge stays current as the operator stages
  // more edits in adjacent tabs.
  //
  // Wave 3 · B7 (v1 memory management): the tray is mounted once in the
  // global header (not per bottom-tab), so `useTabVisible()` here reduces to
  // "is the browser tab itself visible" — still worth pausing on, a hidden
  // browser tab has no business polling every 5-30s. Re-running the effect on
  // the tabVisible flip refreshes immediately on regaining visibility, same
  // pattern as CommandCenter/Suggestions.

  // Remember the rows a run may need to name later (they leave the list as they finish).
  useEffect(() => {
    for (const it of items) if (!jobRowsRef.current.has(it.id)) jobRowsRef.current.set(it.id, it)
  }, [items])

  // Settle a finished "Create all" run: same toast and failed list as Apply.
  const finishedJobRef = useRef<string | null>(null)
  const finishJob = useCallback((job: NewCampaignJob) => {
    if (finishedJobRef.current === job.job_id) return
    finishedJobRef.current = job.job_id
    const out = jobOutcome(job.results)
    const named = (id: number, error: string) => {
      const it = jobRowsRef.current.get(id)
      return it ? { ...it, status: 'failed' as const, error_msg: error } : null
    }
    const nowFailed = [
      ...jobRefusedRef.current,
      ...out.failed.map(f => named(f.id, f.error)).filter((x): x is NonNullable<typeof x> => x != null),
    ]
    // Merge, never replace: this operator's earlier failed pushes stay listed.
    setFailedEdits(prev => [...prev.filter(p => !nowFailed.some(n => n.id === p.id)), ...nowFailed])
    if (out.skipped.length > 0) {
      setActionError(`Not sent, and taken off the list: ${out.skipped.map(sk => {
        const it = jobRowsRef.current.get(sk.id)
        const head = it ? trayHeadline(it) : `edit ${sk.id}`
        return sk.error ? `${head}: ${sk.error}` : head
      }).join(' · ')}`)
    }
    const failedCount = out.failed.length + jobRefusedRef.current.length
    if (failedCount > 0) setToast({ kind: 'failed', failed: failedCount, total: job.total + jobRefusedRef.current.length })
    else setToast({ kind: 'applied', applied: out.applied, staged: out.staged, skipped: out.skipped.length })
    jobRefusedRef.current = []
    setJobId(null); setBusy(null)
    void refresh()
  }, [refresh])

  // PR 24: watch a "Create all" run. Every tick reads the job, moves the
  // "Creating 12 of 33" toast and refetches the list (finished rows leave it);
  // when the job is done the outcome is shown in the usual toast and failed
  // list. A 404 means the server restarted: the rows' own statuses are the truth.
  useApiQuery(
    keys.harvest.newCampaignJob(jobId),
    async () => {
      try {
        const job = await apiEntity.getNewCampaignJob(jobId ?? '')
        if (job.status === 'done') {
          finishJob(job)
        } else {
          setToast({ kind: 'creating', done: job.done, total: job.total, label: job.current?.label ?? null })
          void refetchTray()
        }
        return job
      } catch (e) {
        if (!(isApiError(e) && e.status === 404)) throw e // a blip: the next tick asks again
        setJobId(null); setBusy(null)
        setToast({ kind: 'note', text: 'Lost track of the run (server restarted) — the list shows what happened' })
        void refetchTray()
        return null
      }
    },
    { enabled: !!jobId, poll: CREATE_POLL_MS, retry: 0, staleTime: 0 },
  )

  // Opening the tray mid-run (or after a reload) picks the run up again.
  useEffect(() => {
    if (!open || !active || jobId) return
    let cancelled = false
    apiEntity.listNewCampaignJobs()
      .then(r => {
        if (cancelled || !r.active) return
        // Another operator's run is theirs: do not lock this tray or adopt it.
        if (!ownsRun(r.active, active)) return
        setBusy('create')
        setToast({ kind: 'creating', done: r.active.done, total: r.active.total, label: r.active.current?.label ?? null })
        setJobId(r.active.job_id)
      })
      .catch(() => { /* an older server has no such route: nothing to resume */ })
    return () => { cancelled = true }
  }, [open, active, jobId])

  // Auto-dismiss the RESOLVED toast; 'working' and 'creating' persist until resolved.
  useEffect(() => {
    if (!toast || toast.kind === 'working' || toast.kind === 'creating') return
    const ms = toast.kind === 'failed' || toast.kind === 'note' ? 8000 : 4500
    const t = window.setTimeout(() => setToast(null), ms)
    return () => window.clearTimeout(t)
  }, [toast])

  if (!active) {
    // No operator picked = we do not KNOW the pending count, which is not the
    // same as knowing it is zero. Rendering "0" here told every operator their
    // queue was empty before they had picked themselves — in the one badge the
    // header shows on every screen. An em-dash is the house convention for
    // "unknown"; a real zero still renders as 0 below.
    return (
      <button
        disabled
        className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-ink-faint cursor-not-allowed"
        title="Pick an operator to see pending changes"
        aria-label="Pending changes — unknown until an operator is selected"
      >
        <Inbox size={12} aria-hidden /> <span className="max-sm:sr-only">Pending changes</span> —
      </button>
    )
  }

  const conflictCount = items.filter(i => i.has_conflict).length
  const sendable = sendableIds(items, writes)
  const heldCount = items.length - sendable.length
  // PR 24: "Create all N campaigns" appears for two or more staged new-campaign
  // harvests once both switches are known to be on. One row keeps its own Apply.
  const creatable = createAllIds(items, writes)
  const showCreateAll = creatable.length > 0

  async function onDiscard(id: number) {
    if (!active) return
    setBusy(id); setActionError(null)
    try {
      await apiEntity.discardManualEdit(id, active)
      await refresh()
    } catch (e) { setActionError(String(e)) }
    finally { setBusy(null) }
  }

  // Apply / Apply-all now open the confirm step instead of pushing directly.
  function onRequestApply(id: number) { setConfirmMode('single'); setConfirmIds([id]) }
  function onRequestApplyAll() {
    if (sendable.length === 0) return
    setConfirmMode('all'); setConfirmIds(sendable)
  }

  function onRequestCreateAll() {
    if (!showCreateAll) return
    setConfirmMode('create'); setConfirmIds(creatable)
  }

  // Start the run. The server claims and creates one campaign at a time (the
  // same path as a single Apply); this only starts it and then watches it.
  async function onConfirmCreate() {
    if (!active || confirmIds == null) return
    const ids = confirmIds
    setBusy('create'); setActionError(null)
    setConfirmIds(null)
    setToast({ kind: 'creating', done: 0, total: ids.length, label: null })
    try {
      const r = await apiEntity.applyNewCampaigns(active, ids)
      jobRefusedRef.current = r.refused.map(f => {
        const it = jobRowsRef.current.get(f.id)
        return it ? { ...it, status: 'failed' as const, error_msg: f.reason } : null
      }).filter((x): x is NonNullable<typeof x> => x != null)
      if (jobRefusedRef.current.length) setFailedEdits(jobRefusedRef.current)
      setToast({ kind: 'creating', done: 0, total: r.total, label: null })
      setJobId(r.job_id)
    } catch (e) {
      const refusal = createRefusalMessage(e)
      if (refusal) {
        // Turned away before anything was claimed: not a failure.
        setToast({ kind: 'note', text: refusal })
        void refetchTray()
      } else {
        setActionError(String(e))
        setToast({ kind: 'failed', failed: ids.length, total: ids.length })
      }
      setBusy(null)
    }
  }

  // The only place a push actually fires — after the operator confirms.
  async function onConfirmPush() {
    if (!active || confirmIds == null) return
    const ids = confirmIds
    const mode = confirmMode
    // Snapshot the items being pushed so we can correlate per-edit results and
    // emit the applied-event carrying their new values.
    const pushed = items.filter(i => ids.includes(i.id))
    setBusy(ids.length === 1 ? ids[0] : 'batch'); setActionError(null)
    setConfirmIds(null)
    setToast({ kind: 'working', count: ids.length })
    try {
      // id -> { status, error } from the truthful per-edit backend result.
      const outcome = new Map<number, { status: string; error?: string | null }>()
      if (ids.length === 1) {
        const r = await apiEntity.applyManualEdit(ids[0], active)
        outcome.set(ids[0], { status: r.status, error: r.error_msg })
      } else {
        const r = await apiEntity.applyBatchManualEdits(active, ids)
        for (const res of r.results) {
          const st = res.result?.status ?? (res.ok ? 'applied' : 'failed')
          outcome.set(res.id, { status: st, error: res.result?.error_msg ?? res.error ?? null })
        }
      }
      let applied = 0, staged = 0
      const skippedNotes: string[] = []
      const appliedDetail: AppliedEditDetail[] = []
      const nowFailed: Array<ManualEdit & { error_msg: string | null }> = []
      for (const it of pushed) {
        const o = outcome.get(it.id) ?? { status: 'failed', error: 'no result returned' }
        const bucket = outcomeBucket(o.status)
        if (bucket === 'skipped') {
          // A guard refused it and nothing was sent; the server retired the row.
          skippedNotes.push(o.error ? `${trayHeadline(it)}: ${o.error}` : trayHeadline(it))
        } else if (bucket === 'applied') {
          applied++
          appliedDetail.push({
            entity_type: it.entity_type, entity_id: it.entity_id,
            field: it.field, new_value: it.new_value,
          })
        } else if (bucket === 'staged') {
          staged++
        } else {
          nowFailed.push({ ...it, status: 'failed', error_msg: o.error ?? 'push failed' })
        }
      }
      // Flip matching table cells to the applied value at once (truthful — the
      // write landed on Amazon and is mirrored server-side for reload-safety).
      if (appliedDetail.length) {
        window.dispatchEvent(new CustomEvent(EDITS_APPLIED_EVENT, { detail: appliedDetail }))
      }
      setFailedEdits(nowFailed)
      if (skippedNotes.length > 0) {
        setActionError(`Not sent, and taken off the list: ${skippedNotes.join(' · ')}`)
      }
      if (nowFailed.length > 0) {
        // Failures stay in the tray (with their error) — keep the window OPEN.
        setToast({ kind: 'failed', failed: nowFailed.length, total: ids.length })
      } else {
        setToast({ kind: 'applied', applied, staged, skipped: skippedNotes.length })
        // Apply-all that fully succeeded → close the side window (the toast
        // carries the confirmation). A single confirm keeps it open to keep working.
        if (mode === 'all') setOpen(false)
      }
      await refresh()
    } catch (e) {
      setActionError(String(e))
      setToast({ kind: 'failed', failed: ids.length, total: ids.length })
    } finally { setBusy(null) }
  }

  // Retry a failed push by re-staging it (a fresh pending edit to apply again);
  // dismiss just drops it from the failed list (the DB keeps the 'failed' row
  // as an audit record).
  async function onRetryFailed(it: ManualEdit) {
    if (!active) return
    setBusy(it.id); setActionError(null)
    try {
      await apiEntity.stageManualEdit({
        operator_slug: active,
        entity_type: it.entity_type, entity_id: it.entity_id, field: it.field,
        old_value: it.old_value, new_value: it.new_value,
      })
      setFailedEdits(prev => prev.filter(f => f.id !== it.id))
      await refresh()
    } catch (e) { setActionError(String(e)) }
    finally { setBusy(null) }
  }
  function onDismissFailed(id: number) {
    setFailedEdits(prev => prev.filter(f => f.id !== id))
  }

  const confirmItems = confirmIds == null
    ? []
    : items.filter(i => confirmIds.includes(i.id))
  // PR 24: the "Create all" confirm lists every campaign, with totals.
  const createRows = confirmMode === 'create' && confirmIds != null ? campaignConfirmRows(items, confirmIds) : []

  return (
    <>
      <PendingTrayButton
        buttonRef={buttonRef}
        count={items.length}
        conflicts={conflictCount}
        open={open}
        onClick={() => setOpen(true)}
      />

      {open && (
        <>
          {/* max-w-full: a 24rem tray is wider than a 375 px phone (W0.5). */}
          <div
            ref={drawerRef}
            id="pending-tray"
            role="dialog"
            aria-labelledby="pending-tray-title"
            tabIndex={-1}
            className="fixed top-0 right-0 h-full w-96 max-w-full bg-surface border-l border-edge shadow-pop z-50 flex flex-col outline-none"
          >
            <header className="p-4 border-b border-edge flex items-start justify-between gap-2">
              <div>
                <h2 id="pending-tray-title" className="text-base font-semibold text-ink">Pending changes</h2>
                <p className="text-xs text-ink-muted mt-0.5">{traySubtitle(items.length, conflictCount)}</p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" title="Close (Esc)"
                      className="text-ink-faint hover:text-ink-secondary p-1 -mt-1">
                <X size={18} />
              </button>
            </header>
            {error && (
              <div className="px-4 py-2 text-xs text-rose-700 bg-rose-50 border-b border-rose-200">
                {error}
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              {suggestsQueued > 0 && (
                <Link
                  to="/suggestions?kind=queued"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 border-b border-edge-subtle bg-accent-soft px-4 py-2 text-xs text-accent-soft-fg hover:underline"
                >
                  <Lightbulb size={13} className="shrink-0" />
                  {suggestsQueued} queued in ClickPay Suggests →
                </Link>
              )}
              {failedEdits.length > 0 && (
                <div className="border-b border-rose-200">
                  <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
                    Failed — not applied to Amazon
                  </div>
                  <ul className="divide-y divide-rose-100">
                    {failedEdits.map(it => (
                      <li key={`f-${it.id}`} className="p-3 bg-rose-50/60">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-ink-muted tracking-wide">
                              {trayHeadline(it)}
                            </div>
                            <div className="mt-0.5 text-sm">
                              <span className="font-mono text-gray-500 line-through">{it.old_value ?? '—'}</span>
                              <span className="mx-1 text-gray-400">→</span>
                              <span className="font-mono font-medium text-gray-900">{it.new_value}</span>
                            </div>
                            <div className="mt-1 flex items-start gap-1 text-[11px] text-rose-700">
                              <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                              <span className="break-words">{it.error_msg || 'push failed'}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {canRetryFailed(it) && (
                            <button
                              onClick={() => onRetryFailed(it)}
                              disabled={busy != null}
                              className="text-violet-600 hover:bg-violet-50 p-1 rounded disabled:opacity-50"
                              title="Retry — re-stage this edit"
                            >
                              {busy === it.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                            </button>
                            )}
                            <button
                              onClick={() => onDismissFailed(it.id)}
                              disabled={busy != null}
                              className="text-gray-400 hover:bg-gray-50 p-1 rounded disabled:opacity-50"
                              title="Dismiss"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {items.length === 0 ? (
                failedEdits.length === 0 && (
                  <div className="p-6 text-center text-sm text-gray-500">
                    No pending edits.<br />
                    <span className="text-[11px] text-gray-400">
                      Edits stage here when you change a bid, budget, or state on any analytics tab.
                    </span>
                  </div>
                )
              ) : (
                <ul className="divide-y divide-gray-100">
                  {items.map(it => (
                    <li
                      key={it.id}
                      className={`p-3 ${it.has_conflict ? 'bg-rose-50/40' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-500 uppercase tracking-wider">
                            {trayHeadline(it)}
                          </div>
                          {/* Identifier block — campaign / product / ad group, then ids.
                              Ad-group NAME comes from ppc_ad_group_definitions (falls
                              back to id-only line until that sync has run). A staged
                              harvest says where the term came from and where it goes. */}
                          {it.entity_type === 'harvest' ? (
                            <div className="mt-0.5 text-[11px] text-gray-600 space-y-0.5">
                              {harvestRowLines(it.context).map(line => (
                                <div key={line} className="truncate" title={line}>{line}</div>
                              ))}
                              {it.context?.asin && (
                                <div className="truncate">
                                  <span className="text-gray-400">ASIN:</span>{' '}
                                  <CopyId value={it.context.asin} label="ASIN">
                                    <span className="font-mono">{it.context.asin}</span>
                                  </CopyId>
                                </div>
                              )}
                            </div>
                          ) : (
                          <div className="mt-0.5 text-[11px] text-gray-600 space-y-0.5">
                            {it.context?.campaign_name && (
                              <div className="truncate" title={it.context.campaign_name}>
                                <span className="text-gray-400">Campaign:</span> {it.context.campaign_name}
                              </div>
                            )}
                            {(it.context?.asin || it.context?.ad_group_name) && (
                              <div className="truncate">
                                {it.context?.asin && (
                                  <><span className="text-gray-400">ASIN:</span>{' '}
                                    <CopyId value={it.context.asin} label="ASIN">
                                      <span className="font-mono">{it.context.asin}</span>
                                    </CopyId></>
                                )}
                                {it.context?.asin && it.context?.ad_group_name && (
                                  <span className="text-gray-300"> | </span>
                                )}
                                {it.context?.ad_group_name && (
                                  <><span className="text-gray-400">Ad Group:</span> {it.context.ad_group_name}</>
                                )}
                              </div>
                            )}
                            <div className="truncate font-mono text-[10px] text-gray-400" title={it.entity_id}>
                              <span className="text-gray-400">{entityIdLabel(it.entity_type)}:</span> {entityIdText(it.entity_type, it.entity_id)}
                              {it.context?.ad_group_id && (
                                <><span className="text-gray-300"> | </span>
                                  <span className="text-gray-400">Ad Group ID:</span> {it.context.ad_group_id}</>
                              )}
                            </div>
                          </div>
                          )}
                          <div className="mt-1 text-sm">
                            <span className="font-mono text-gray-500 line-through">{it.old_value ?? '—'}</span>
                            <span className="mx-1 text-gray-400">→</span>
                            <span className="font-mono font-medium text-gray-900">{it.new_value}</span>
                          </div>
                          {it.has_conflict && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-rose-700 font-medium uppercase">
                              <AlertTriangle size={10} /> conflict — another operator has a staged edit
                            </div>
                          )}
                          {it.notes && (
                            <div className="mt-1 text-[11px] text-gray-500 italic">{it.notes}</div>
                          )}
                          {heldRowChip(it, writes) && (
                            <Badge tone="neutral" className="mt-1 whitespace-normal text-[11px]">{heldRowChip(it, writes)}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {!isHeldTrayRow(it, writes) && (
                            <button
                              onClick={() => onRequestApply(it.id)}
                              disabled={busy != null}
                              className="text-emerald-600 hover:bg-emerald-50 p-1 rounded disabled:opacity-50"
                              title="Apply (confirm before push)"
                            >
                              {busy === it.id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            </button>
                          )}
                          <button
                            onClick={() => onDiscard(it.id)}
                            disabled={busy != null}
                            className="text-rose-600 hover:bg-rose-50 p-1 rounded disabled:opacity-50"
                            title="Discard"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* ── Recent changes (owner ask 2026-07-07; collapsed since #98):
                    activity log below the pending queue, scoped Mine / Others
                    / All. Read-only, and only read while expanded. ── */}
              <RecentChangesSection
                open={recentOpen}
                onToggle={toggleRecent}
                scope={recentScope}
                onScope={setRecentScope}
                loading={recentQ.isPending && recentQ.fetchStatus !== 'idle'}
                recent={recent}
                active={active}
              />
            </div>
            {/* Apply all counts only rows ClickPay can send: held listing
                image updates wait in the list above and are never pushed. */}
            {(sendable.length > 0 || showCreateAll) && (
              <footer className="p-3 border-t border-gray-200 flex gap-2">
                {sendable.length > 0 && (
                  <button
                    onClick={onRequestApplyAll}
                    disabled={busy != null}
                    className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    title={heldCount > 0 ? (heldSummary(items, writes) ?? undefined) : undefined}
                  >
                    {busy === 'batch' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    {heldCount > 0 ? `Apply all ${sendable.length}` : 'Apply all'}
                  </button>
                )}
                {showCreateAll && (
                  <CreateAllButton count={creatable.length} busy={busy === 'create'}
                                   disabled={busy != null} onClick={onRequestCreateAll} />
                )}
              </footer>
            )}
          </div>
        </>
      )}

      {confirmIds != null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-scrim" onClick={() => setConfirmIds(null)} aria-hidden />
          <div
            ref={confirmRef}
            role="dialog"
            aria-modal="true"
            aria-label="Confirm push to Amazon"
            tabIndex={-1}
            className="relative bg-white rounded-lg shadow-2xl border border-gray-200 w-[28rem] max-w-[calc(100vw-1rem)] max-h-[80vh] flex flex-col outline-none"
          >
            <header className="p-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">
                {confirmMode === 'create'
                  ? `Create ${createRows.length} campaigns on Amazon?`
                  : `Push ${confirmItems.length} change${confirmItems.length === 1 ? '' : 's'} to Amazon?`}
              </h2>
            </header>
            {/* Live-writes posture banner — read-only display, drives nothing. */}
            <div
              className={`px-4 py-2 text-xs border-b ${
                liveWrites === null
                  ? 'bg-gray-50 border-gray-200 text-gray-500'
                  : liveWrites
                    ? 'bg-rose-50 border-rose-200 text-rose-700'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}
            >
              {liveWrites === null
                ? 'Checking live-write status…'
                : liveWrites
                  ? 'LIVE WRITES ENABLED — confirming will push real changes to Amazon.'
                  : 'Live writes are OFF — these will stage (no Amazon call fires).'}
            </div>
            {confirmMode === 'create' ? (
              <CreateAllConfirmBody rows={createRows} />
            ) : (
            <div className="flex-1 overflow-y-auto p-2">
              <ul className="divide-y divide-gray-100">
                {confirmItems.map(it => (
                  <li key={it.id} className="p-2">
                    <div className="text-[11px] text-gray-500 uppercase tracking-wider">
                      {trayHeadline(it)}
                    </div>
                    {it.entity_type !== 'harvest' && (
                      <div className="text-xs font-mono text-gray-700 truncate" title={it.entity_id}>
                        {it.entity_id}
                      </div>
                    )}
                    <div className="mt-0.5 text-sm">
                      <span className="font-mono text-gray-500 line-through">{it.old_value ?? '—'}</span>
                      <span className="mx-1 text-gray-400">→</span>
                      <span className="font-mono font-medium text-gray-900">{it.new_value}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            )}
            {error && (
              <div className="px-4 py-2 text-xs text-rose-700 bg-rose-50 border-t border-rose-200">
                {error}
              </div>
            )}
            <footer className="p-3 border-t border-gray-200 flex gap-2">
              <button
                onClick={() => setConfirmIds(null)}
                disabled={busy != null}
                className="flex-1 px-3 py-1.5 text-sm font-medium rounded border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmMode === 'create' ? onConfirmCreate : onConfirmPush}
                disabled={busy != null}
                className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {busy != null ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {confirmMode === 'create'
                  ? `Create ${createRows.length} campaigns`
                  : `Push ${confirmItems.length} change${confirmItems.length === 1 ? '' : 's'}`}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Push-lifecycle toast — bottom-right. Working spinner → resolves to a
          purple "applied to Amazon" (only when a real write landed), amber
          "staged" (writes off), or rose "failed". Copy is driven strictly by
          the backend's per-edit status, so it can never claim a write that
          didn't happen. */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-[70] animate-[fade-in_180ms_ease-out]" data-overlay-ignore>
          <div className={`flex items-center gap-2.5 rounded-lg px-4 py-3 shadow-2xl border min-w-[16rem] transition-colors ${
            toast.kind === 'working' || toast.kind === 'creating' || toast.kind === 'note'
              ? 'bg-white border-gray-200 text-gray-700'
              : toast.kind === 'applied'
                ? (toast.applied > 0
                    ? 'bg-violet-600 border-violet-700 text-white'
                    : 'bg-amber-50 border-amber-300 text-amber-900')
                : 'bg-rose-50 border-rose-300 text-rose-800'
          }`}>
            {toast.kind === 'working' && (
              <>
                <Loader2 size={18} className="animate-spin text-violet-600 shrink-0" />
                <span className="text-sm font-medium">
                  Pushing {toast.count} change{toast.count === 1 ? '' : 's'} to Amazon…
                </span>
              </>
            )}
            {toast.kind === 'creating' && (
              <>
                <Loader2 size={18} className="animate-spin text-violet-600 shrink-0" />
                <span className="text-sm font-medium">{creatingToastText(toast.done, toast.total, toast.label)}</span>
              </>
            )}
            {toast.kind === 'note' && (
              <span className="text-sm font-medium">{toast.text}</span>
            )}
            {toast.kind === 'applied' && (
              <>
                {toast.applied > 0
                  ? <CheckCircle2 size={18} className="text-white shrink-0" />
                  : <Check size={18} className="text-amber-700 shrink-0" />}
                <span className="text-sm font-medium">
                  {toast.applied > 0 && toast.staged === 0 &&
                    `${toast.applied} change${toast.applied === 1 ? '' : 's'} applied to Amazon`}
                  {toast.applied > 0 && toast.staged > 0 &&
                    `${toast.applied} applied · ${toast.staged} staged`}
                  {toast.applied === 0 && toast.staged > 0 &&
                    `${toast.staged} staged — live writes off, no Amazon change`}
                  {toast.applied === 0 && toast.staged === 0 && !toast.skipped && 'Nothing to apply'}
                  {!!toast.skipped && `${toast.applied + toast.staged > 0 ? ' · ' : ''}${toast.skipped} not sent (see why)`}
                </span>
              </>
            )}
            {toast.kind === 'failed' && (
              <>
                <AlertTriangle size={18} className="text-rose-600 shrink-0" />
                <span className="text-sm font-medium">
                  {toast.failed} of {toast.total} failed — see the tray
                </span>
              </>
            )}
            {toast.kind !== 'working' && toast.kind !== 'creating' && (
              <button
                onClick={() => setToast(null)}
                className="ml-1 opacity-70 hover:opacity-100"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  )
}

/** The header button: "Pending changes" + count, tinted by what is waiting
 *  (semantic tokens; it was raw amber/rose/gray and an unlabelled number). */
export function PendingTrayButton({
  count, conflicts, open, onClick, buttonRef,
}: {
  count: number
  conflicts: number
  open: boolean
  onClick: () => void
  buttonRef?: RefObject<HTMLButtonElement | null>
}) {
  const tone = conflicts > 0
    ? 'bg-danger-soft border-danger-edge text-danger-soft-fg hover:opacity-90'
    : count > 0
      ? 'bg-warning-soft border-warning-edge text-warning-soft-fg hover:opacity-90'
      : 'bg-surface-2 border-edge text-ink-muted hover:bg-surface-3'
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      data-testid="pending-tray-button"
      aria-label={trayButtonLabel(count)}
      aria-haspopup="dialog"
      aria-expanded={open}
      className={cn('inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded border', tone)}
      title={`${count} pending change${count === 1 ? '' : 's'}${conflicts ? ` · ${conflicts} conflict${conflicts === 1 ? '' : 's'}` : ''}`}
    >
      {conflicts > 0 ? <AlertTriangle size={12} aria-hidden /> : <Inbox size={12} aria-hidden />}
      <span className="max-sm:sr-only">Pending changes</span>
      <span className={cn('tabular-nums', count === 0 && 'text-ink-faint')}>{count}</span>
    </button>
  )
}

/** "Recent changes": a disclosure, collapsed by default (#98). The scope
 *  toggle and the list exist only while it is expanded. */
export function RecentChangesSection({
  open, onToggle, scope, onScope, loading, recent, active,
}: {
  open: boolean
  onToggle: () => void
  scope: RecentScope
  onScope: (s: RecentScope) => void
  loading: boolean
  recent: ManualEdit[]
  active: string | null
}) {
  return (
    <div className="border-t border-edge" data-recent-changes={open ? 'open' : 'closed'}>
      <div className="px-3 pt-3 pb-2 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-muted hover:text-ink"
        >
          {open ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
          Recent changes
        </button>
        {open && (
          <div
            className="inline-flex rounded border border-edge overflow-hidden"
            title="Whose change history to show below: yours, everyone else's, or everyone's"
          >
            {(['mine', 'others', 'all'] as RecentScope[]).map(s => (
              <button
                key={s}
                type="button"
                onClick={() => onScope(s)}
                aria-pressed={scope === s}
                className={cn(
                  'px-2 py-0.5 text-[10px] font-medium capitalize',
                  scope === s ? 'bg-accent text-accent-fg' : 'bg-surface text-ink-secondary hover:bg-surface-2',
                )}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      {open && (loading && recent.length === 0 ? (
        <div className="px-3 pb-4 text-[11px] text-ink-faint">Loading recent changes…</div>
      ) : recent.length === 0 ? (
        <div className="px-3 pb-4 text-[11px] text-ink-faint">
          No recent changes{scope === 'others' ? ' by other operators' : ''}.
        </div>
      ) : (
        <ul className="divide-y divide-edge-subtle">
          {recent.map(it => (
            <li key={`r-${it.id}`} className="px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] text-ink-muted uppercase tracking-wider truncate">
                  {trayHeadline(it)}
                </div>
                <span
                  className={cn('shrink-0 px-1.5 py-px rounded border text-[9px] font-medium',
                    RECENT_STATUS_STYLE[it.status] ?? RECENT_STATUS_FALLBACK)}
                  title={it.status === 'failed' && it.error_msg ? it.error_msg : undefined}
                >
                  {recentStatusLabel(it.status)}
                </span>
              </div>
              {(it.context?.campaign_name || it.context?.asin) && (
                <div className="mt-0.5 text-[11px] text-ink-secondary truncate" title={it.context?.campaign_name ?? undefined}>
                  {it.context?.campaign_name}
                  {it.context?.campaign_name && it.context?.asin && <span className="text-ink-faint"> | </span>}
                  {it.context?.asin && (
                    <CopyId value={it.context.asin} label="ASIN">
                      <span className="font-mono">{it.context.asin}</span>
                    </CopyId>
                  )}
                </div>
              )}
              <div className="mt-0.5 flex items-center justify-between gap-2">
                <div className="text-xs truncate">
                  <span className="font-mono text-ink-muted line-through">{it.old_value ?? '—'}</span>
                  <span className="mx-1 text-ink-faint">→</span>
                  <span className="font-mono font-medium text-ink">{it.new_value}</span>
                </div>
                <div className="shrink-0 inline-flex items-center gap-1 text-[10px] text-ink-muted">
                  <span
                    className="w-2 h-2 rounded-full border border-edge"
                    style={{ backgroundColor: it.operator_color ?? 'var(--color-ink-faint)' }}
                  />
                  <span className="max-w-[7rem] truncate">
                    {it.operator_slug === active ? 'you' : (it.operator_display ?? it.operator_slug)}
                  </span>
                  <span className="text-ink-faint">·</span>
                  <span title={it.applied_at ?? it.staged_at ?? undefined}>
                    {relTime(it.applied_at ?? it.staged_at)}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ))}
    </div>
  )
}
